import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/pages/portal/PortalDashboard";
import { X } from "lucide-react";

const STATUSES = ["new", "in_review", "active", "resolved", "closed"];

export default function AdminRequests() {
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [status, setStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/case-requests").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const open = (c) => {
        setSelected(c);
        setStatus(c.status);
        setNotes(c.admin_notes || "");
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/case-requests/${selected.id}`, { status, admin_notes: notes });
            toast.success("Case updated");
            setSelected(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-requests-page">
            <div className="label-tag">Case requests</div>
            <h1 className="mt-3 font-display text-4xl font-bold">Inbound case requests</h1>
            <div className="mt-8 bg-ink-900 border border-white/10 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-ink-800 text-xs uppercase tracking-[0.2em] text-white/50">
                        <tr>
                            <th className="text-left px-5 py-4">Title</th>
                            <th className="text-left px-5 py-4">Union</th>
                            <th className="text-left px-5 py-4">Service</th>
                            <th className="text-left px-5 py-4">Urgency</th>
                            <th className="text-left px-5 py-4">Status</th>
                            <th className="text-left px-5 py-4">Opened</th>
                            <th className="text-left px-5 py-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-white/50 py-10">No requests yet.</td></tr>
                        ) : items.map((c) => (
                            <tr key={c.id} className="border-t border-white/10" data-testid={`admin-req-row-${c.id}`}>
                                <td className="px-5 py-4">{c.title}</td>
                                <td className="px-5 py-4 text-white/70">{c.union_name || c.user_email}</td>
                                <td className="px-5 py-4 text-white/60">{c.service_slug}</td>
                                <td className="px-5 py-4 capitalize text-white/70">{c.urgency}</td>
                                <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                                <td className="px-5 py-4 text-white/50">{new Date(c.created_at).toLocaleDateString()}</td>
                                <td className="px-5 py-4">
                                    <button onClick={() => open(c)} data-testid={`admin-req-edit-${c.id}`} className="text-gold-500 hover:text-gold-400 text-xs uppercase tracking-[0.2em]">Manage →</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-xl bg-ink-800 border border-gold-500/30 rounded-2xl p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-req-modal">
                        <div className="flex justify-between items-start gap-4">
                            <div>
                                <div className="label-tag">Case</div>
                                <h2 className="mt-2 font-display text-2xl font-bold">{selected.title}</h2>
                                <div className="mt-2 text-xs text-white/50">{selected.user_email} · {selected.union_name}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-white/50 hover:text-white"><X /></button>
                        </div>
                        <p className="mt-5 text-sm text-white/70 whitespace-pre-wrap">{selected.description}</p>
                        <div className="mt-6">
                            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Status</div>
                            <div className="flex flex-wrap gap-2">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setStatus(s)}
                                        data-testid={`admin-status-${s}`}
                                        className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full border ${
                                            status === s ? "bg-gold-500 text-ink-900 border-gold-500" : "bg-ink-900 text-white/70 border-white/15"
                                        }`}
                                    >
                                        {s.replace("_", " ")}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="block mt-6">
                            <span className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Admin notes</span>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="admin-notes" className="w-full bg-ink-900 border border-white/15 rounded-lg px-4 py-3 text-white resize-none" />
                        </label>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="admin-save-btn"
                            className="mt-6 w-full px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400 disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
